<script lang="ts" setup>
import { computed } from 'vue'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselControlsBackgroundPrev,
  CarouselControlsBackgroundNext
  , BlockContainer, Separator
} from '@toni/brand-ui'

import { usePageStore } from '../../../stores/page'
import { useConfigStore } from '../../../stores/config'
import {
  ChildTagsTeaserGroup,
  ChildTagsTeaserAvatar,
  ChildTagsTeaserTitle
} from '../../ui/child-tag-teaser-group'
import { useContentBlock } from '../../../content-blocks/composables/use-content-block'
import { createImgSrc, getImgDefinitions } from '../../bauer-image/image-utils'
import { usePageBlockWidth } from '../../../composables/use-page-block-width.ts'

import type { ChildTagsTeaserSchema } from '../../../utils/content-block-schema/teaser-schema'

const props = defineProps<{
  id: string
}>()

const id = computed(() => props.id)
const contentBlock = useContentBlock(id.value, 'childTagsTeaserGroup')

const pageStore = usePageStore()
const brand = usePageStore().brandFromStore as string
const imageServiceUrl = useConfigStore().rsConfig.imageServiceUrl as string
const imageSrcDefinitions = useConfigStore().rsConfig.imageSrcDefinitions
const setName = 'bx-teaser-container--child-tags' as string
const imageDefinitions = getImgDefinitions(brand, imageSrcDefinitions, setName)
const imgFallbackWidth = imageDefinitions.maxWidth ?? imageSrcDefinitions?.maxWidth ?? 0

const isHome = computed(() => pageStore.isHome as boolean)
const size = computed(() => usePageBlockWidth(pageStore.pageData?.pageMeta, isHome.value, brand).value)

const teasers = computed(() =>
  contentBlock.value.data.childTagsTeaserGroup.teasers?.filter(teaser => teaser.titleLong)
)

const imageSrc = (teaser: ChildTagsTeaserSchema) => {
  if (!teaser) return undefined
  // These are only mobile sizes for now. SrcSets will be implemented with https://bauer-specialist.atlassian.net/jira/software/c/projects/TCDW/boards/229?selectedIssue=TCDW-345
  return createImgSrc({
    image: teaser,
    brand,
    imageServiceUrl,
    setName,
    srcSetDefinition: {
      format: 'square'
    }
  }, imgFallbackWidth)?.href ?? ''
}
</script>

<template>
  <ChildTagsTeaserGroup>
    <BlockContainer :size="size"
                    class="my-6 md:my-8">
      <Carousel variant="simple"
                align="start"
                class="pb-6"
                :opts="{
                  dragFree: true
                }">
        <CarouselContent class="md:gap-6">
          <CarouselItem v-for="teaser in teasers"
                        :key="teaser.assetId"
                        class="max-w-16 md:max-w-24 md">
            <a :href="teaser.url"
               :target="teaser.target"
               class="flex flex-col items-center gap-2 cursor-pointer md:items-start md:gap-3">
              <ChildTagsTeaserAvatar :img-src="imageSrc({ ...teaser, target: undefined })"
                                     :alt="teaser.imageAltText"
                                     size="ctt" />
              <ChildTagsTeaserTitle>
                {{ teaser.titleLong }}
              </ChildTagsTeaserTitle>
            </a>
          </CarouselItem>
        </CarouselContent>
        <CarouselControlsBackgroundPrev :show-when-cannot-scroll="false">
          <CarouselPrevious class="h-4 w-4"
                            size="sm"
                            :use-disabled-opacity="false"
                            :show-when-cannot-scroll="false" />
        </CarouselControlsBackgroundPrev>
        <CarouselControlsBackgroundNext :show-when-cannot-scroll="false">
          <CarouselNext class="h-4 w-4"
                        size="sm"
                        :use-disabled-opacity="false"
                        :show-when-cannot-scroll="false" />
        </CarouselControlsBackgroundNext>
      </Carousel>
      <Separator />
    </BlockContainer>
  </ChildTagsTeaserGroup>
</template>
